import { useCallback, useEffect, useRef } from "react";
import {
	useNavigate as useRouterNavigate,
	useLocation,
} from "react-router-dom";
import { AppScreen, useApp } from "../components/AppContext";

// Map AppScreen values to route paths
const screenToPath: Record<AppScreen, string> = {
	// Auth screens
	"auth-sign-in": "/auth/signin",
	"auth-create-account": "/auth/create",
	"auth-verify-email": "/auth/verify-email",
	"auth-phone-number": "/auth/phone",
	"auth-verify-phone": "/auth/verify-phone",
	"auth-setup-password": "/auth/password",
	"auth-complete-profile": "/auth/complete-profile",

	// Main dashboard
	dashboard: "/dashboard",
	"my-event": "/events/my-event",

	// Profile screens
	"app-profile-completion": "/profile/complete",
	"occupation-proof": "/profile/occupation-proof",
	"app-profile-completion-overview": "/profile/overview",
	"app-payroll-data": "/profile/payroll-data",
	"app-prm-data": "/profile/prm",
	"app-contract-data": "/profile/contract",
	"app-payroll-info": "/profile/payroll",
	"work-permit-proof": "/profile/work-permit-proof",

	// Events
	"app-event-application": "/events/apply",
	"app-event-details-1": "/events/details/1",
	"app-event-details-2": "/events/details/2",

	"app-group-select": "/group/select",
	"app-group-create": "/group/create",
	"app-group-join": "/group/join",
	"app-group": "/group",

	// Other screens
	"app-notifications": "/notifications",
	"app-contract": "/contract",
	"app-info-faq": "/info/faq",
	"app-settings": "/settings",
	"app-code-of-conduct": "/code-of-conduct",

	// Operations
	"operations-check-in": "/operations/check-in",
	"operations-check-out": "/operations/check-out",

	// Onboarding
	"onboarding-welcome": "/onboarding/welcome",
	"onboarding-step-profile": "/onboarding/profile",
	"onboarding-step-contract-data": "/onboarding/contract",
	"onboarding-step-event-application": "/onboarding/event",
	"onboarding-step-code-of-conduct": "/onboarding/conduct",
	"onboarding-step-payroll-info": "/onboarding/payroll",
	"onboarding-step-id-check": "/onboarding/id-check",

	// Intro screens
	"intro-profile-completion": "/intro/profile",
	"intro-contract-data": "/intro/contract",
	"intro-event-application": "/intro/event",
	"intro-code-of-conduct": "/intro/conduct",
	"intro-payroll-info": "/intro/payroll",
	"intro-id-check": "/intro/id-check",

	// Legacy screens (maintain compatibility)
	"profile-data": "/legacy/profile-data",
	"financial-data": "/legacy/financial-data",
	"my-profile": "/legacy/my-profile",
	"billing-data": "/legacy/billing-data",
	"payroll-document": "/legacy/payroll-document",
	"job-detail": "/legacy/job-detail",
	"task-list": "/task-list",
	settings: "/legacy/settings",
	notifications: "/legacy/notifications",
	appointments: "/legacy/appointments",
	"webinar-booking": "/legacy/webinar-booking",
	"training-booking": "/legacy/training-booking",
	"check-in-list": "/legacy/check-in-list",
	"check-out-list": "/legacy/check-out-list",
	apply: "/legacy/apply",
	"id-terms": "/legacy/id-terms",
	"id-capture": "/legacy/id-capture",
	"id-result": "/legacy/id-result",

	// User account management
	"delete-my-account": "/account/delete",
};

// Reverse mapping from path to screen
const pathToScreen: Record<string, AppScreen> = Object.entries(
	screenToPath
).reduce(
	(acc, [screen, path]) => ({ ...acc, [path]: screen as AppScreen }),
	{}
);

/**
 * Custom hook that bridges React Router with the existing Context API navigation
 * This allows for gradual migration from Context-based to Router-based navigation
 */
export function useAppRouter() {
	const routerNavigate = useRouterNavigate();
	const location = useLocation();
	const isSyncing = useRef(false);
	const {
		currentScreen,
		setCurrentScreen,
		canAccessScreen,
		isAuthenticated,
		navigateDirectly: contextNavigate,
		navigateWithProcessCheck: contextNavigateWithCheck,
	} = useApp();

	// Sync URL with context screen changes
	useEffect(() => {
		if (isSyncing.current) return;

		// Don't sync if we're on a special route like reset-password, or have token in URL
		if (location.pathname === "/reset-password") {
			return;
		}

		// Don't sync if we have a token in the URL (invitation or password reset)
		const urlParams = new URLSearchParams(location.search);
		if (urlParams.has("token")) {
			return;
		}

		const pathForCurrentScreen = screenToPath[currentScreen];
		if (pathForCurrentScreen && location.pathname !== pathForCurrentScreen) {
			isSyncing.current = true;
			routerNavigate(pathForCurrentScreen, { replace: true });
			setTimeout(() => {
				isSyncing.current = false;
			}, 0);
		}
	}, [currentScreen, location.pathname, location.search, routerNavigate]);

	// Sync context screen with URL changes
	useEffect(() => {
		if (isSyncing.current) return;

		const screenForCurrentPath = pathToScreen[location.pathname];
		if (screenForCurrentPath && screenForCurrentPath !== currentScreen) {
			isSyncing.current = true;
			setCurrentScreen(screenForCurrentPath);
			setTimeout(() => {
				isSyncing.current = false;
			}, 0);
		}
	}, [location.pathname, currentScreen, setCurrentScreen]);

	/**
	 * Navigate to a screen using React Router
	 * Also updates the context screen for compatibility
	 */
	const navigate = useCallback(
		(screen: AppScreen) => {
			const path = screenToPath[screen];
			if (path) {
				isSyncing.current = true;
				// Update both context and router
				contextNavigate(screen);
				routerNavigate(path);
				setTimeout(() => {
					isSyncing.current = false;
				}, 0);
			} else {
				// Fallback to context navigation if no route defined
				contextNavigate(screen);
			}
		},
		[contextNavigate, routerNavigate]
	);

	/**
	 * Navigate with access check
	 */
	const navigateWithCheck = useCallback(
		(screen: AppScreen) => {
			const accessCheck = canAccessScreen(screen);

			if (accessCheck.allowed) {
				navigate(screen);
			} else if (accessCheck.redirectTo) {
				navigate(accessCheck.redirectTo);
			}
		},
		[canAccessScreen, navigate]
	);

	/**
	 * Navigate back in history
	 */
	const goBack = useCallback(() => {
		routerNavigate(-1);
	}, [routerNavigate]);

	/**
	 * Replace current history entry
	 */
	const replace = useCallback(
		(screen: AppScreen) => {
			const path = screenToPath[screen];
			if (path) {
				isSyncing.current = true;
				contextNavigate(screen);
				routerNavigate(path, { replace: true });
				setTimeout(() => {
					isSyncing.current = false;
				}, 0);
			} else {
				contextNavigate(screen);
			}
		},
		[contextNavigate, routerNavigate]
	);

	return {
		navigate,
		navigateWithCheck,
		goBack,
		replace,
		currentPath: location.pathname,
		isPath: (path: string) => location.pathname === path,
	};
}
